import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  ErrorOutline as ErrorIcon,
  HourglassTop as QueuedIcon,
  PauseCircleOutline as PausedIcon,
  PlayArrow as RunIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import apiService from '../services/api.service';
import ResponsiveTable from '../components/common/ResponsiveTable';
import {
  McsrSyncPeriod,
  McsrSyncRunResult,
  McsrSyncScheduleStatus,
  McsrSyncStatusPayload,
} from '../types/mcsr';

/**
 * MCSR Sync Monitor — after preparing a new period in the SAP B1 MCSR Addon,
 * the user starts all MCSR sync schedules from here and visually watches every
 * data source import into the application, until the new period shows up in
 * the "Periods in application" panel. Runs are queued to the background
 * scheduler (they start within a minute); the page auto-refreshes while
 * anything is running or queued.
 */

const KIND_LABEL: Record<string, string> = {
  addon: 'MCSR Addon table',
  document: 'SAP document',
  master: 'SAP master data',
};

/** Effective display state: an Idle schedule whose last run failed reads as Failed. */
function displayState(s: McsrSyncScheduleStatus): 'Running' | 'Queued' | 'OK' | 'Failed' | 'Paused' | 'Missing' | 'Never run' {
  if (s.state === 'Running') return 'Running';
  if (s.state === 'Queued') return 'Queued';
  if (s.state === 'Paused') return 'Paused';
  if (s.state === 'Missing') return 'Missing';
  if (!s.lastRun) return 'Never run';
  return s.lastRun.status === 'Failure' ? 'Failed' : 'OK';
}

function StateChip({ s }: { s: McsrSyncScheduleStatus }) {
  const state = displayState(s);
  switch (state) {
    case 'Running':
      return <Chip size="small" color="info" icon={<CircularProgress size={12} color="inherit" />} label="Running" />;
    case 'Queued':
      return <Chip size="small" color="warning" icon={<QueuedIcon />} label="Queued" />;
    case 'OK':
      return <Chip size="small" color="success" icon={<OkIcon />} label="OK" />;
    case 'Failed':
      return (
        <Tooltip title={s.lastRun?.error || 'Last run failed'}>
          <Chip size="small" color="error" icon={<ErrorIcon />} label="Failed" />
        </Tooltip>
      );
    case 'Paused':
      return (
        <Tooltip title="Schedule is paused (auto-paused when the MCSR addon is missing, or paused by an admin). Press Run to resume it.">
          <Chip size="small" icon={<PausedIcon />} label="Paused" />
        </Tooltip>
      );
    case 'Missing':
      return (
        <Tooltip title="No sync schedule exists for this data source in the selected company — it is normally created automatically at deploy time.">
          <Chip size="small" color="error" variant="outlined" label="No schedule" />
        </Tooltip>
      );
    default:
      return <Chip size="small" variant="outlined" label="Never run" />;
  }
}

function fmtWhen(isoStr: string | null): string {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1 && diffMs >= 0) return 'just now';
  if (mins >= 1 && mins < 60) return `${mins}m ago`;
  if (mins >= 60 && mins < 48 * 60) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleString();
}

/** 2026-04-01 → APR-2026 (the SAP MCSR period naming). */
function fmtPeriod(isoMonth: string): string {
  const d = new Date(`${isoMonth.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return isoMonth;
  return `${d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' }).toUpperCase()}-${d.getUTCFullYear()}`;
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

interface SummaryCardProps {
  label: string;
  value: string;
  color?: string;
  sub?: string;
}

function SummaryCard({ label, value, color, sub }: SummaryCardProps) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ py: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{label}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: color || 'text.primary' }}>{value}</Typography>
        {sub && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

export default function McsrSyncMonitor() {
  const [status, setStatus] = useState<McsrSyncStatusPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [runningIds, setRunningIds] = useState<number[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await apiService.getMcsrSyncStatus();
      setStatus(res.data as McsrSyncStatusPayload);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load MCSR sync status');
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  const anyActive = useMemo(
    () => (status?.schedules || []).some((s) => s.state === 'Running' || s.state === 'Queued'),
    [status]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Auto-refresh: fast while imports are running/queued, relaxed otherwise.
  useEffect(() => {
    const timer = setInterval(refresh, anyActive ? 5000 : 20000);
    return () => clearInterval(timer);
  }, [refresh, anyActive]);

  const startAll = useCallback(async () => {
    setStarting(true);
    try {
      const res = await apiService.runMcsrSync();
      const result = res.data as McsrSyncRunResult;
      setToast(
        result.queued.length
          ? `${result.queued.length} sync job${result.queued.length === 1 ? '' : 's'} queued — imports start within a minute`
          : 'No active MCSR schedules to run for this company'
      );
      await refresh();
    } catch (e: any) {
      setToast(e?.response?.data?.message || 'Failed to start MCSR sync');
    } finally {
      setStarting(false);
    }
  }, [refresh]);

  const runOne = useCallback(
    async (s: McsrSyncScheduleStatus) => {
      if (!s.scheduleId) return;
      setRunningIds((ids) => [...ids, s.scheduleId as number]);
      try {
        const res = await apiService.runMcsrSync([s.scheduleId]);
        const result = res.data as McsrSyncRunResult;
        if (result.skipped.length) {
          setToast(result.skipped[0].reason);
        } else {
          setToast(`${s.label} queued${result.resumed.length ? ' (schedule resumed)' : ''} — starts within a minute`);
        }
        await refresh();
      } catch (e: any) {
        setToast(e?.response?.data?.message || `Failed to queue ${s.label}`);
      } finally {
        setRunningIds((ids) => ids.filter((id) => id !== s.scheduleId));
      }
    },
    [refresh]
  );

  const schedules = useMemo(() => status?.schedules || [], [status]);
  const periods = status?.periods || [];
  const counts = useMemo(() => {
    const c = { running: 0, queued: 0, ok: 0, failed: 0, attention: 0 };
    for (const s of schedules) {
      const st = displayState(s);
      if (st === 'Running') c.running += 1;
      else if (st === 'Queued') c.queued += 1;
      else if (st === 'OK') c.ok += 1;
      else if (st === 'Failed') c.failed += 1;
      else c.attention += 1; // Paused / Missing / Never run
    }
    return c;
  }, [schedules]);
  const latestPeriod = periods[0];

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>MCSR Sync Monitor</Typography>
          <Typography variant="body2" color="text.secondary">
            Start and watch the import of MCSR data from SAP B1 into the application. After preparing a new
            period in the MCSR Addon, press “Sync All Now” and wait until every source shows OK and the new
            period appears below.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh now">
            <span>
              <IconButton onClick={refresh} disabled={loading} aria-label="Refresh sync status">
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={starting ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
            onClick={startAll}
            disabled={starting}
          >
            Sync All Now
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {anyActive && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Import in progress — this page refreshes automatically every 5 seconds.
        </Alert>
      )}

      {loading && !status ? (
        <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryCard label="Sources OK" value={String(counts.ok)} color="success.main" />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryCard label="Running" value={String(counts.running)} color={counts.running ? 'info.main' : undefined} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryCard label="Queued" value={String(counts.queued)} color={counts.queued ? 'warning.main' : undefined} />
            </Grid>
            <Grid item xs={6} sm={4} md={2.4}>
              <SummaryCard label="Failed" value={String(counts.failed)} color={counts.failed ? 'error.main' : undefined} />
            </Grid>
            <Grid item xs={12} sm={8} md={2.4}>
              <SummaryCard
                label="Latest period in application"
                value={latestPeriod ? fmtPeriod(latestPeriod.month) : '—'}
                sub={latestPeriod ? `${latestPeriod.projects} projects · imported ${fmtWhen(latestPeriod.lastImportedAt)}` : 'No MCSR data yet'}
              />
            </Grid>
          </Grid>

          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Data sources</Typography>
            <ResponsiveTable<McsrSyncScheduleStatus>
              ariaLabel="MCSR data sources"
              rows={schedules}
              getRowKey={(s) => s.scheduleId ?? s.endpointKey}
              columns={[
                {
                  id: 'source',
                  header: 'Data Source',
                  cell: (s) => (
                    <Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{s.label}</Typography>
                      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{KIND_LABEL[s.kind] || s.kind}</Typography>
                    </Box>
                  ),
                },
                { id: 'state', header: 'Status', cell: (s) => <StateChip s={s} /> },
                {
                  id: 'lastrun',
                  header: 'Last Run',
                  cell: (s) =>
                    s.lastRun ? (
                      <Box>
                        <Typography sx={{ fontSize: 13 }}>{fmtWhen(s.lastRun.finishedAt || s.lastRun.startedAt)}</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                          {s.lastRun.rows !== null ? `${fmtInt(s.lastRun.rows)} rows` : s.lastRun.status}
                        </Typography>
                      </Box>
                    ) : '—',
                },
                { id: 'rows', header: 'Rows in App', align: 'right', cell: (s) => fmtInt(s.rowsInApp) },
                {
                  id: 'latest',
                  header: 'Latest Data',
                  cell: (s) =>
                    s.latestDataDate ? (
                      <Tooltip title={s.endpointKey === 'sap-mcsr-headers' ? 'Latest MCSR period' : 'Latest document date'}>
                        <span>
                          {s.endpointKey === 'sap-mcsr-headers' ? fmtPeriod(s.latestDataDate) : s.latestDataDate}
                        </span>
                      </Tooltip>
                    ) : '—',
                  hideOnMobile: true,
                },
                {
                  id: 'backfill',
                  header: 'Backfill',
                  cell: (s) =>
                    s.backfill
                      ? s.backfill.complete
                        ? <Chip size="small" variant="outlined" color="success" label="Complete" />
                        : <Tooltip title={`History loading — cursor at ${s.backfill.cursor ? s.backfill.cursor.slice(0, 10) : 'start'}`}>
                            <Chip size="small" variant="outlined" color="warning" label="In progress" />
                          </Tooltip>
                      : '—',
                  hideOnMobile: true,
                },
                {
                  id: 'run',
                  header: 'Run',
                  align: 'center',
                  cell: (s) => {
                    if (!s.scheduleId) return '—';
                    const busy = runningIds.includes(s.scheduleId) || s.state === 'Running' || s.state === 'Queued';
                    return (
                      <Tooltip title={s.state === 'Paused' ? 'Resume and run this sync' : 'Run this sync now'}>
                        <span>
                          <IconButton size="small" onClick={() => runOne(s)} disabled={busy} aria-label={`Run ${s.label}`}>
                            {busy ? <CircularProgress size={16} /> : <RunIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    );
                  },
                },
              ]}
              emptyMessage="No MCSR sync schedules found for the selected company"
            />
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>Periods in application</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Every MCSR period currently imported, with the number of project snapshots and cost lines it
              carries. When the new period you generated in SAP appears here with all its projects, the import
              is complete and MCSR users can run the report on it.
            </Typography>
            <ResponsiveTable<McsrSyncPeriod>
              ariaLabel="MCSR periods in application"
              rows={periods}
              getRowKey={(p) => p.month}
              columns={[
                {
                  id: 'month',
                  header: 'Period',
                  cell: (p, idx) => (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{fmtPeriod(p.month)}</Typography>
                      {idx === 0 && <Chip size="small" color="primary" variant="outlined" label="Latest" />}
                    </Stack>
                  ),
                },
                { id: 'projects', header: 'Projects', align: 'right', cell: (p) => fmtInt(p.projects) },
                { id: 'snapshots', header: 'Snapshots', align: 'right', cell: (p) => fmtInt(p.snapshots) },
                { id: 'lines', header: 'Cost Lines', align: 'right', cell: (p) => fmtInt(p.costLines) },
                { id: 'imported', header: 'Last Imported', cell: (p) => fmtWhen(p.lastImportedAt), hideOnMobile: true },
              ]}
              emptyMessage="No MCSR periods imported yet — run a sync to bring in the addon data"
            />
          </Paper>
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}
